<template>
  <section class="company-container tw-page">
    <div class="tw-container">
      <PageHeader
        title="Company"
        subtitle="View and manage the information tied to your organization."
      >
        <template #actions>
          <b-button type="is-light" tag="router-link" to="/company/business-context">
            Business Context
          </b-button>
        </template>
      </PageHeader>

      <div v-if="isLoading" class="tw-state">
        <b-loading :is-full-page="false" :active="true" />
        <p>Loading company details...</p>
      </div>

      <div v-else-if="error" class="tw-state tw-state--error">
        <p>{{ error }}</p>
      </div>

      <div v-else-if="company">
        <b-tabs v-model="activeTab" class="company-tabs" type="is-toggle-rounded">
          <b-tab-item label="Company Info">
            <div class="company-card tw-card">
              <div class="company-details">
                <div class="detail">
                  <span class="detail-label">Company ID</span>
                  <p class="detail-value">{{ company?._id || '—' }}</p>
                </div>
                <div class="detail">
                  <span class="detail-label">Tenant Owner Email</span>
                  <p class="detail-value">{{ company?.admin?.email || '—' }}</p>
                </div>
                <div class="detail">
                  <span class="detail-label">Tenant Owner Name</span>
                  <p class="detail-value">{{ formatAdminName(company?.admin) }}</p>
                </div>
                <div class="detail">
                  <span class="detail-label">Company Handle(s)</span>
                  <p class="detail-value">{{ handleDisplay }}</p>
                </div>
                <div class="detail invite-detail">
                  <span class="detail-label">Invite Link</span>
                  <div class="invite-display">
                    <span class="detail-value">{{ inviteLink || '—' }}</span>
                    <b-button
                      type="is-primary"
                      :disabled="!inviteLink"
                      size="is-small"
                      @click="copyInviteLink"
                    >
                      Copy
                    </b-button>
                    <span v-if="copySuccess" class="copy-confirm">Copied!</span>
                  </div>
                </div>
              </div>

              <div class="company-form">
                <b-field label="Handle(s)">
                  <div class="handle-repeater">
                    <div
                      v-for="(h, idx) in company.handle"
                      :key="idx"
                      class="handle-repeater__row"
                    >
                      <b-input
                        v-model="company.handle[idx]"
                        placeholder="acme.com"
                        :disabled="!canUseSuperUserTestControls"
                        expanded
                      />
                      <b-button
                        v-if="canUseSuperUserTestControls"
                        type="is-light"
                        icon-left="close"
                        :disabled="company.handle.length === 1"
                        @click="removeHandle(idx)"
                      />
                    </div>
                    <b-button
                      v-if="canUseSuperUserTestControls"
                      type="is-light"
                      icon-left="plus"
                      size="is-small"
                      @click="addHandle"
                    >
                      Add handle
                    </b-button>
                    <p v-else class="help">
                      The handle is your company's email domain and decides who can ask to join. Contact Teamwrkr to change it.
                    </p>
                  </div>
                </b-field>

                <b-field label="Address Line 1">
                  <b-input v-model="company.address1" placeholder="Address line 1" />
                </b-field>

                <b-field label="Address Line 2">
                  <b-input v-model="company.address2" placeholder="Address line 2" />
                </b-field>

                <div class="company-form-row">
                  <b-field label="City">
                    <b-input v-model="company.city" placeholder="City" />
                  </b-field>

                  <b-field label="State">
                    <b-input v-model="company.state" placeholder="State" />
                  </b-field>

                  <b-field label="Zip">
                    <b-input v-model="company.zip" placeholder="Zip / Postal Code" />
                  </b-field>
                </div>

                <b-field label="Phone">
                  <b-input v-model="company.phone" placeholder="Phone number" />
                </b-field>

                <b-field label="Last Working Day recipients" grouped>
                  <b-input
                    v-model="company.teamsEmail"
                    placeholder="teams-connector@yourcompany.com, ops@yourcompany.com"
                    expanded
                  />
                  <p class="control">
                    <b-button
                      :disabled="!hasValidLwdRecipients || isSendingTestEmail"
                      :loading="isSendingTestEmail"
                      @click="sendTestEmail"
                    >
                      Test
                    </b-button>
                  </p>
                  <p v-if="canUseSuperUserTestControls" class="control">
                    <b-button
                      :disabled="!hasValidLwdRecipients || isSendingReportTestEmail"
                      :loading="isSendingReportTestEmail"
                      @click="sendReportTestEmail"
                    >
                      Test with Report
                    </b-button>
                  </p>
                </b-field>
                <span
                  v-if="testEmailStatus === 'success'"
                  class="save-message success"
                >
                  Test email sent.
                </span>
                <span
                  v-else-if="testEmailStatus === 'error'"
                  class="save-message error"
                >
                  Unable to send test email. Please try again.
                </span>
                <span
                  v-if="reportTestEmailStatus === 'success'"
                  class="save-message success"
                >
                  Test email with report sent.
                </span>
                <span
                  v-else-if="reportTestEmailStatus === 'no-report'"
                  class="save-message error"
                >
                  No Last Working Day report found for this company yet.
                </span>
                <span
                  v-else-if="reportTestEmailStatus === 'error'"
                  class="save-message error"
                >
                  Unable to send test email. Please try again.
                </span>

                <b-field
                  label="Daily Email"
                  custom-class="lwd-daily-email-field"
                >
                  <div class="lwd-daily-email-control">
                    <b-checkbox
                      v-model="company.sendLwdReportDaily"
                      :disabled="!hasValidLwdRecipients"
                    >
                      Email the Last Working Day report to these recipients every night
                    </b-checkbox>
                    <p v-if="!hasValidLwdRecipients" class="lwd-daily-email-help">
                      Add at least one valid recipient above to enable daily email delivery.
                    </p>
                  </div>
                </b-field>

                <b-field label="PSA System">
                  <b-select v-model="psaType" placeholder="Select a PSA">
                    <option value="connectwise">ConnectWise</option>
                    <option value="kaseya">Kaseya (Autotask + IT Glue)</option>
                  </b-select>
                </b-field>

                <template v-if="psaType === 'connectwise'">
                  <b-field label="ConnectWise API Base Url">
                    <b-input
                      v-model="company.cwApiBaseUrl"
                      placeholder="https://yourcompany.connectwise.com"
                    />
                  </b-field>

                  <div class="company-form-row">
                    <b-field label="ConnectWise Client ID">
                      <b-input
                        v-model="company.cwClientId"
                        placeholder="ConnectWise client ID"
                      />
                    </b-field>

                    <b-field label="ConnectWise Company ID">
                      <b-input
                        v-model="company.cwCompanyId"
                        placeholder="ConnectWise company ID"
                      />
                    </b-field>
                  </div>

                  <div class="company-form-row">
                    <b-field label="ConnectWise Public Key">
                      <b-input
                        v-model="company.cwPublicKey"
                        type="password"
                        password-reveal
                        :placeholder="company.cwCredentialsConfigured ? '(stored — leave blank to keep)' : 'ConnectWise public key'"
                      />
                    </b-field>

                    <b-field label="ConnectWise Private Key">
                      <b-input
                        v-model="company.cwPrivateKey"
                        type="password"
                        password-reveal
                        :placeholder="company.cwCredentialsConfigured ? '(stored — leave blank to keep)' : 'ConnectWise private key'"
                      />
                    </b-field>
                  </div>

                </template>

                <template v-else-if="psaType === 'kaseya'">
                  <b-field label="Autotask API Base URL">
                    <b-input
                      v-model="company.atApiUrl"
                      placeholder="https://webservices2.autotask.net/ATServicesRest/V1.0 (leave blank to auto-detect)"
                    />
                  </b-field>

                  <div class="company-form-row">
                    <b-field label="Autotask API User">
                      <b-input
                        v-model="company.atApiUser"
                        placeholder="Autotask API user email"
                      />
                    </b-field>

                    <b-field label="Autotask Tracking Identifier">
                      <b-input
                        v-model="company.atTrackingIdentifier"
                        type="password"
                        password-reveal
                        :placeholder="company.atCredentialsConfigured ? '(stored — leave blank to keep)' : 'Autotask API Integration Code'"
                      />
                    </b-field>
                  </div>

                  <b-field label="Autotask API Secret">
                    <b-input
                      v-model="company.atApiSecret"
                      type="password"
                      password-reveal
                      :placeholder="company.atCredentialsConfigured ? '(stored — leave blank to keep)' : 'Autotask API secret'"
                    />
                  </b-field>

                  <b-field label="IT Glue API Key">
                    <b-input
                      v-model="company.itGlueApiKey"
                      type="password"
                      password-reveal
                      :placeholder="company.itGlueCredentialsConfigured ? '(stored — leave blank to keep)' : 'IT Glue API key'"
                    />
                  </b-field>

                  <p class="daily-work-sync-help">
                    IT Glue account context syncs nightly once a key is saved.
                  </p>
                  <p v-if="company.cwCredentialsConfigured" class="daily-work-sync-help daily-work-sync-help--warning">
                    <i class="mdi mdi-alert"></i>
                    Saving with Kaseya selected stops nightly ConnectWise ticket sync for this
                    company — Autotask ticket sync (below) starts in its place, pulling from a
                    different source, so historical ticket continuity across the switch isn't
                    guaranteed.
                  </p>
                </template>

                <b-field
                  label="Nightly Ticket Sync"
                  custom-class="daily-work-sync-field"
                >
                  <div class="daily-work-sync-control">
                    <b-checkbox
                      v-model="company.dailyWorkSync"
                      :disabled="!isDailyWorkSyncEligible"
                    >
                      Enable nightly ticket sync from {{ activePsaLabel }}
                    </b-checkbox>
                    <p v-if="dailyWorkSyncMessage" class="daily-work-sync-help">
                      {{ dailyWorkSyncMessage }}
                    </p>
                  </div>
                </b-field>

                <b-field
                  label="Company Context Signals"
                  custom-class="signals-enabled-field"
                >
                  <div class="signals-enabled-control">
                    <b-checkbox v-model="company.signalsEnabled">
                      Enable nightly signal detection (owner departures, backlog risk, workload
                      concentration, etc.) for this company
                    </b-checkbox>
                    <p class="signals-enabled-help">
                      Turn off as a fallback if a signal looks wrong for this company — stops new
                      findings from being generated without affecting the rest of Company
                      Context.
                    </p>
                  </div>
                </b-field>

                <b-field label="About / Description">
                  <b-input
                    v-model="company.description"
                    type="textarea"
                    placeholder="Describe your company"
                    rows="4"
                  />
                </b-field>
              </div>

              <div class="form-actions">
                <b-button
                  type="is-primary"
                  :loading="isSaving"
                  :disabled="isSaving"
                  @click="saveCompany"
                >
                  Save
                </b-button>
                <span
                  v-if="saveStatus === 'success'"
                  class="save-message success"
                >
                  Company updated successfully.
                </span>
                <span
                  v-else-if="saveStatus === 'error'"
                  class="save-message error"
                >
                  Unable to update company. Please try again.
                </span>
              </div>
            </div>
          </b-tab-item>

          <b-tab-item label="Employees">
            <div class="company-card employees-card tw-card">
              <div v-if="isLoading" class="tw-state">
                <b-loading :is-full-page="false" :active="true" />
                <p>Loading employees...</p>
              </div>
              <div v-else>
                <b-table
                  :data="employeesList"
                  :striped="true"
                  :hoverable="true"
                  :paginated="employeesList.length > 10"
                  :per-page="10"
                  class="company-employees-table"
                >
                  <template #empty>
                    <div class="empty-msg">No employees to display.</div>
                  </template>

                  <b-table-column field="name" label="Name" v-slot="props">
                    {{ formatEmployeeName(props.row) }}
                  </b-table-column>

                  <b-table-column field="email" label="Email" v-slot="props">
                    {{ props.row.email || '—' }}
                  </b-table-column>

                  <!-- <b-table-column field="role" label="Role" v-slot="props">
                    {{ formatEmployeeRole(props.row) }}
                  </b-table-column> -->
                </b-table>
              </div>
            </div>
          </b-tab-item>

          <!-- spec-multi-admin-per-company: admins (and super users) see who has access here.
               spec-tenant-owner: only the tenant owner (or a super user) can grant/revoke admin;
               financial access stays with super users and admins who hold it. -->
          <b-tab-item v-if="canManageAccess" label="Admins & access">
            <div class="company-card tw-card">
              <AdminsAccess
                :config="config"
                :company-id="company?.id || company?._id || null"
                @self-demoted="onSelfDemoted"
              />
            </div>
          </b-tab-item>

          <!-- spec-admin-gated-company-access: the owner / admins approve who joins the company. -->
          <b-tab-item v-if="canManageAccess" label="Access requests">
            <div class="company-card tw-card">
              <AccessRequests
                :config="config"
                :company-id="company?.id || company?._id || null"
              />
            </div>
          </b-tab-item>

          <b-tab-item label="Work Sync">
            <div class="company-card tw-card work-sync-card">

              <!-- Trigger -->
              <div class="work-sync-section">
                <h3 class="work-sync-section__title">Ad-Hoc Ticket Ingestion</h3>
                <p class="work-sync-section__desc">
                  Pull tickets from {{ activePsaLabel }} for a specific date window.
                </p>

                <div v-if="!hasConfiguredActivePsaCredentials" class="work-sync-notice">
                  <i class="mdi mdi-alert-circle-outline"></i>
                  {{ activePsaLabel }} credentials are not fully configured. Complete them in the Company Info tab first.
                </div>

                <div v-else class="work-sync-form">
                  <div class="work-sync-form__row">
                    <b-field label="Start Date">
                      <b-input
                        v-model="syncStartDate"
                        type="date"
                        :disabled="isSyncRunning"
                      />
                    </b-field>
                    <b-field label="End Date">
                      <b-input
                        v-model="syncEndDate"
                        type="date"
                        :disabled="isSyncRunning"
                      />
                    </b-field>
                  </div>

                  <div class="work-sync-form__actions">
                    <b-button
                      type="is-primary"
                      :loading="syncLoading"
                      :disabled="syncLoading || isSyncRunning || !syncStartDate || !syncEndDate"
                      @click="startIngestion"
                    >
                      <i class="mdi mdi-play"></i>
                      {{ isSyncRunning ? 'Ingestion Running…' : 'Start Ingestion' }}
                    </b-button>
                  </div>

                  <p v-if="syncError" class="work-sync-error">
                    <i class="mdi mdi-alert-circle"></i> {{ syncError }}
                  </p>
                </div>
              </div>

              <!-- Status -->
              <div v-if="syncJob" class="work-sync-section work-sync-status">
                <h3 class="work-sync-section__title">Job Status</h3>

                <div class="work-sync-status__header">
                  <span class="work-sync-status__badge" :class="`is-${syncJob.status}`">
                    {{ syncStatusLabel }}
                  </span>
                  <span class="work-sync-status__id">Job {{ syncJob.jobId }}</span>
                  <b-button
                    v-if="isSyncRunning"
                    size="is-small"
                    type="is-light"
                    :loading="syncPolling"
                    @click="fetchJobStatus(syncJob.jobId)"
                  >
                    Refresh
                  </b-button>
                </div>

                <div v-if="syncJob.progress && isSyncRunning" class="work-sync-progress">
                  <progress
                    class="progress is-primary"
                    :value="syncJob.progress.percent || 0"
                    max="100"
                  >{{ syncJob.progress.percent || 0 }}%</progress>
                  <p v-if="syncJob.progress.message" class="work-sync-progress__msg">
                    {{ syncJob.progress.message }}
                  </p>
                  <p v-else-if="syncJob.progress.totalTickets > 0" class="work-sync-progress__msg">
                    {{ syncJob.progress.processedTickets }} / {{ syncJob.progress.totalTickets }} tickets
                  </p>
                </div>

                <div v-if="syncJob.result && !isSyncRunning" class="work-sync-results">
                  <div class="work-sync-results__grid">
                    <div class="work-sync-results__stat">
                      <span class="work-sync-results__num">{{ syncJob.result.insertedTickets || 0 }}</span>
                      <span class="work-sync-results__label">Tickets</span>
                    </div>
                    <div class="work-sync-results__stat">
                      <span class="work-sync-results__num">{{ syncJob.result.insertedArtifacts || 0 }}</span>
                      <span class="work-sync-results__label">Artifacts</span>
                    </div>
                    <div class="work-sync-results__stat">
                      <span class="work-sync-results__num">{{ syncJob.result.updatedUsers || 0 }}</span>
                      <span class="work-sync-results__label">Users Updated</span>
                    </div>
                    <div class="work-sync-results__stat">
                      <span class="work-sync-results__num">{{ syncJob.result.duplicateTicketsSkipped || 0 }}</span>
                      <span class="work-sync-results__label">Duplicates Skipped</span>
                    </div>
                  </div>
                  <ul v-if="syncJob.result.errors && syncJob.result.errors.length" class="work-sync-results__errors">
                    <li v-for="(e, i) in syncJob.result.errors" :key="i">{{ e }}</li>
                  </ul>
                </div>

                <p v-if="syncJob.lastError && syncJob.status === 'failed'" class="work-sync-error">
                  <i class="mdi mdi-alert-circle"></i> {{ syncJob.lastError }}
                </p>
              </div>

            </div>
          </b-tab-item>
        </b-tabs>
      </div>
    </div>
  </section>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader.vue'
import AdminsAccess from '@/components/company/AdminsAccess.vue'
import AccessRequests from '@/components/company/AccessRequests.vue'
import { useUserStore } from '@/stores/userStore'
import { mapState } from 'pinia'

export default {
  name: 'CompanyAdmin',
  components: {
    PageHeader,
    AdminsAccess,
    AccessRequests,
  },
  data() {
    return {
      company: null,
      companyEmployees: [],
      isLoading: false,
      error: null,
      isSaving: false,
      saveStatus: null,
      isSendingTestEmail: false,
      testEmailStatus: null,
      isSendingReportTestEmail: false,
      reportTestEmailStatus: null,
      copySuccess: false,
      lastCompanyId: null,
      activeTab: 0,
      psaType: 'connectwise',
      requiredCwFields: [
        'cwApiBaseUrl',
        'cwClientId',
        'cwCompanyId',
        'cwPrivateKey',
        'cwPublicKey',
      ],
      config: {
        headers: { Authorization: this.$cookies.get('teamwrkr-token') },
      },
      syncStartDate: '',
      syncEndDate: '',
      syncJob: null,
      syncLoading: false,
      syncPolling: false,
      syncPollingTimer: null,
      syncError: null,
    }
  },
  computed: {
    ...mapState(useUserStore, ['user', 'isImpersonating', 'impersonatorIsSuperUser']),
    canManageAccess() {
      return this.user?.isAdmin === true || this.user?.isSuperUser === true
    },
    canUseSuperUserTestControls() {
      return this.user?.isSuperUser === true || (this.isImpersonating && this.impersonatorIsSuperUser === true)
    },
    // The company whose page is open: a super user may open any company via ?companyId=;
    // everyone else always sees their own.
    currentCompanyId() {
      const requested = `${this.$route?.query?.companyId || ''}`.trim()
      if (requested && this.user?.isSuperUser === true) return requested
      if (!this.user?.company) return null
      return typeof this.user.company === 'object'
        ? this.user.company._id || null
        : this.user.company
    },
    hasRequiredCwFields() {
      return this.requiredCwFields.every((field) => {
        if (field === 'cwPrivateKey' || field === 'cwPublicKey') {
          // Neither is ever returned by the API (company.controllers.js strips real secret
          // values from every response) — treat as set if the backend confirms credentials
          // are configured.
          return this.hasNonEmptyValue(this.company?.[field]) || this.company?.cwCredentialsConfigured === true
        }
        return this.hasNonEmptyValue(this.company?.[field])
      })
    },
    hasConfiguredCwCredentials() {
      if (!this.hasRequiredCwFields) return false
      if (typeof this.company?.cwCredentialsConfigured === 'boolean') {
        return this.company.cwCredentialsConfigured
      }

      return true
    },
    // Story 12.4 — Work Sync's ad-hoc trigger follows whichever PSA is active (psaType), same
    // as Company Info's own dropdown. Unlike ConnectWise, Autotask has no client-side required-
    // fields computed property to fall back on (hasRequiredCwFields's equivalent) — the backend's
    // atCredentialsConfigured flag is the only source of truth here, which is fine since it's
    // already fully server-verified.
    hasConfiguredActivePsaCredentials() {
      return this.psaType === 'kaseya'
        ? this.company?.atCredentialsConfigured === true
        : this.hasConfiguredCwCredentials
    },
    activePsaLabel() {
      return this.psaType === 'kaseya' ? 'Autotask' : 'ConnectWise'
    },
    isSyncRunning() {
      return this.syncJob && ['queued', 'processing'].includes(this.syncJob.status)
    },
    syncStatusLabel() {
      const map = {
        queued: 'Queued',
        processing: 'Processing',
        completed: 'Completed',
        completed_with_errors: 'Completed with Errors',
        failed: 'Failed',
      }
      return map[this.syncJob?.status] || this.syncJob?.status || ''
    },
    // Story 12.4 — follows whichever PSA is active (same as Work Sync's ad-hoc trigger), since
    // the nightly scheduler now dispatches this exact company.dailyWorkSync flag to either PSA
    // based on resolveActiveTicketPsa, not just ConnectWise.
    isDailyWorkSyncEligible() {
      if (typeof this.company?.dailyWorkSyncEligible === 'boolean') {
        return this.company.dailyWorkSyncEligible
      }

      return this.hasConfiguredActivePsaCredentials
    },
    dailyWorkSyncMessage() {
      if (this.isDailyWorkSyncEligible) return ''
      return `Complete all ${this.activePsaLabel} API credentials to enable nightly ticket sync.`
    },
    handleDisplay() {
      const handles = Array.isArray(this.company?.handle) ? this.company.handle : (this.company?.handle ? [this.company.handle] : []);
      return handles.filter(Boolean).join(', ') || '—';
    },
    inviteLink() {
      const handles = Array.isArray(this.company?.handle) ? this.company.handle : (this.company?.handle ? [this.company.handle] : []);
      const first = handles.find(Boolean);
      if (!first) return '';
      const baseUrl = process.env.VUE_APP_BASE_URL || window.location.origin;
      return `${baseUrl}/welcome/${first}`;
    },
    employeesList() {
      if (Array.isArray(this.companyEmployees) && this.companyEmployees.length) {
        return this.companyEmployees
      }
      return Array.isArray(this.company?.employees) ? this.company.employees : []
    },
    lwdRecipients() {
      const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return `${this.company?.teamsEmail || ''}`
        .split(',')
        .map(email => email.trim())
        .filter(email => email && EMAIL_RE.test(email))
    },
    hasValidLwdRecipients() {
      return this.lwdRecipients.length > 0
    },
  },
  watch: {
    user: {
      handler(newUser) {
        if (!newUser || !newUser._id) return
        if (!newUser.isAdmin && !newUser.isSuperUser) {
          this.error = 'You must be an admin to view this page.'
          return
        }

        const companyId = this.currentCompanyId

        if (!companyId) {
          this.error = 'No company is associated with your account.'
          return
        }

        this.error = null

        if (this.lastCompanyId === companyId && this.company) {
          return
        }

        this.lastCompanyId = companyId
        this.fetchCompany()
      },
      immediate: true,
    },
    company: {
      handler() {
        if (this.saveStatus) this.saveStatus = null
      },
      deep: true,
    },
    isDailyWorkSyncEligible(isEligible) {
      if (isEligible || !this.company?.dailyWorkSync) return

      this.company.dailyWorkSync = false
    },
    hasValidLwdRecipients(isEligible) {
      if (isEligible || !this.company?.sendLwdReportDaily) return

      this.company.sendLwdReportDaily = false
    },
  },
  beforeCreate() {
    if (!this.$cookies.isKey('teamwrkr-token')) {
      this.$router.push('/verify')
    }
  },
  created() {
    const userStore = useUserStore()
    const token = this.$cookies.get('teamwrkr-token')
    userStore.fetchUser(token)
    const today = new Date()
    const thirtyDaysAgo = new Date(today)
    thirtyDaysAgo.setDate(today.getDate() - 30)
    this.syncEndDate = today.toISOString().slice(0, 10)
    this.syncStartDate = thirtyDaysAgo.toISOString().slice(0, 10)
  },
  beforeUnmount() {
    this.stopPolling()
  },
  mounted() {
    if (!this.$cookies.isKey('teamwrkr-token')) {
      this.$router.push('/verify')
      return
    }

    if (this.user && !this.user.isAdmin && !this.user.isSuperUser) {
      this.$router.push('/')
    }
  },
  methods: {
    // An admin who removed their own admin flag loses this page: refresh the user so the
    // existing isAdmin watcher shows the "must be an admin" state.
    onSelfDemoted() {
      useUserStore().fetchUser(this.$cookies.get('teamwrkr-token'), { force: true })
    },
    async fetchCompany() {
      if (!this.currentCompanyId) return

      this.isLoading = true
      this.error = null

      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_API_URL}/companies/${this.currentCompanyId}`,
          this.config,
        )
        const payload = response?.data || {}
        const company = payload.company || payload || {}
        this.company = {
          ...company,
          dailyWorkSync: Boolean(company.dailyWorkSync),
          sendLwdReportDaily: Boolean(company.sendLwdReportDaily),
          // Absent reads as enabled (matches the backend default/semantics), not disabled —
          // see companyContextInference.scheduler.js's own company?.signalsEnabled !== false.
          signalsEnabled: company.signalsEnabled !== false,
          handle: Array.isArray(company.handle)
            ? company.handle
            : (company.handle ? [company.handle] : ['']),
        }

        const employeesPayload =
          payload.employees ||
          payload.empolyees ||
          this.company?.employees ||
          []

        this.companyEmployees = Array.isArray(employeesPayload) ? employeesPayload : []

        // An explicit, persisted activeTicketPsa (Story 12.9) always wins. Otherwise default to
        // whichever this company already has configured — ConnectWise wins if both happen to be
        // set, since it's the more common existing case (mirrors resolveActiveTicketPsa's own
        // fallback on the backend).
        if (this.company.activeTicketPsa === 'autotask') {
          this.psaType = 'kaseya'
        } else if (this.company.activeTicketPsa === 'connectwise') {
          this.psaType = 'connectwise'
        } else {
          this.psaType = this.company.cwCredentialsConfigured
            ? 'connectwise'
            : (this.company.atCredentialsConfigured || this.company.itGlueCredentialsConfigured)
              ? 'kaseya'
              : 'connectwise'
        }
      } catch (fetchError) {
        console.error('Error fetching company:', fetchError)
        this.error = 'Unable to load company information.'
      } finally {
        this.isLoading = false
      }
    },
    async saveCompany() {
      if (!this.company?._id) return

      this.isSaving = true
      this.saveStatus = null

      const payload = {
        address1: this.company.address1,
        address2: this.company.address2,
        city: this.company.city,
        state: this.company.state,
        zip: this.company.zip,
        phone: this.company.phone,
        teamsEmail: this.company.teamsEmail,
        cwApiBaseUrl: this.company.cwApiBaseUrl,
        cwClientId: this.company.cwClientId,
        cwCompanyId: this.company.cwCompanyId,
        ...(this.company.cwPrivateKey ? { cwPrivateKey: this.company.cwPrivateKey } : {}),
        ...(this.company.cwPublicKey ? { cwPublicKey: this.company.cwPublicKey } : {}),
        // Always included regardless of which PSA is selected in the dropdown — it's a display
        // toggle, not a deletion switch. IT Glue in particular can be configured alongside
        // either ticketing PSA, so saving from the ConnectWise view must not wipe it (or vice
        // versa). Secret fields are only sent when non-blank, same pattern as cwPrivateKey.
        atApiUrl: this.company.atApiUrl,
        atApiUser: this.company.atApiUser,
        ...(this.company.atApiSecret ? { atApiSecret: this.company.atApiSecret } : {}),
        ...(this.company.atTrackingIdentifier ? { atTrackingIdentifier: this.company.atTrackingIdentifier } : {}),
        ...(this.company.itGlueApiKey ? { itGlueApiKey: this.company.itGlueApiKey } : {}),
        // Story 12.9 — unlike the credential fields above, this DOES follow the dropdown: it's
        // the persisted signal the nightly scheduler uses to pick one ticket source when both
        // ConnectWise and Autotask credentials are on file, so it must reflect what's currently
        // selected, not always both/neither.
        activeTicketPsa: this.psaType === 'kaseya' ? 'autotask' : 'connectwise',
        dailyWorkSync: this.isDailyWorkSyncEligible && Boolean(this.company.dailyWorkSync),
        sendLwdReportDaily: this.hasValidLwdRecipients && Boolean(this.company.sendLwdReportDaily),
        signalsEnabled: this.company.signalsEnabled !== false,
        description: this.company.description,
        handle: (this.company.handle || []).map(h => h.trim().toLowerCase()).filter(Boolean),
      }

      try {
        await this.axios.patch(
          `${process.env.VUE_APP_API_URL}/companies/${this.company._id}`,
          payload,
          this.config,
        )
        this.saveStatus = 'success'
      } catch (saveError) {
        console.error('Error updating company:', saveError)
        this.saveStatus = 'error'
      } finally {
        this.isSaving = false
      }
    },
    async sendTestEmail() {
      if (!this.hasValidLwdRecipients || !this.company?._id) return

      this.isSendingTestEmail = true
      this.testEmailStatus = null

      try {
        await this.axios.post(
          `${process.env.VUE_APP_API_URL}/companies/${this.company._id}/test-lwd-email`,
          { email: this.company.teamsEmail },
          this.config,
        )
        this.testEmailStatus = 'success'
      } catch (testEmailError) {
        console.error('Error sending LWD report test email:', testEmailError)
        this.testEmailStatus = 'error'
      } finally {
        this.isSendingTestEmail = false
      }
    },
    async sendReportTestEmail() {
      if (!this.hasValidLwdRecipients || !this.company?._id) return

      this.isSendingReportTestEmail = true
      this.reportTestEmailStatus = null

      try {
        await this.axios.post(
          `${process.env.VUE_APP_API_URL}/companies/${this.company._id}/test-lwd-email-with-report`,
          { email: this.company.teamsEmail },
          this.config,
        )
        this.reportTestEmailStatus = 'success'
      } catch (testEmailError) {
        console.error('Error sending LWD report test email with report:', testEmailError)
        this.reportTestEmailStatus = testEmailError?.response?.status === 404 ? 'no-report' : 'error'
      } finally {
        this.isSendingReportTestEmail = false
      }
    },
    async copyInviteLink() {
      if (!this.inviteLink) return

      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(this.inviteLink)
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = this.inviteLink
          textarea.setAttribute('readonly', '')
          textarea.style.position = 'absolute'
          textarea.style.left = '-9999px'
          document.body.appendChild(textarea)
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }
        this.copySuccess = true
        setTimeout(() => (this.copySuccess = false), 2000)
      } catch (copyError) {
        console.error('Unable to copy invite link:', copyError)
      }
    },
    formatAdminName(admin) {
      if (!admin) return '—'
      const first = admin.firstName || ''
      const last = admin.lastName || ''
      const fullName = `${first} ${last}`.trim()
      return fullName || '—'
    },
    formatEmployeeName(employee) {
      if (!employee) return '—'
      const fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim()
      return fullName || employee.name || employee.email || '—'
    },
    formatEmployeeRole(employee) {
      if (!employee) return '—'
      return (
        employee.role ||
        employee.title ||
        employee.jobTitle ||
        employee.position ||
        '—'
      )
    },
    addHandle() {
      if (!Array.isArray(this.company.handle)) this.company.handle = [];
      this.company.handle.push('');
    },
    removeHandle(idx) {
      this.company.handle.splice(idx, 1);
    },
    hasNonEmptyValue(value) {
      if (value === null || value === undefined) return false
      return String(value).trim().length > 0
    },

    async startIngestion() {
      if (!this.company?._id) return
      this.syncLoading = true
      this.syncError = null
      const token = this.$cookies.get('teamwrkr-token')
      try {
        const params = new URLSearchParams({
          startDate: this.syncStartDate,
          endDate: this.syncEndDate,
        })
        const endpoint = this.psaType === 'kaseya' ? 'autotask' : 'connectwise'
        const res = await this.axios.post(
          `${process.env.VUE_APP_API_URL}/work/ingest/${endpoint}/api/ingest/${this.company._id}?${params}`,
          {},
          { headers: { Authorization: token } }
        )
        this.syncJob = res.data
        this.startPolling(res.data.jobId)
      } catch (err) {
        const status = err?.response?.status
        const msg = err?.response?.data?.message
        if (status === 409) {
          const activeJobId = err.response.data.activeJobId
          this.syncError = 'An ingestion is already running.'
          if (activeJobId) {
            this.syncJob = { jobId: activeJobId, status: 'processing', progress: null, result: null }
            this.startPolling(activeJobId)
          }
        } else {
          this.syncError = msg || 'Failed to start ingestion.'
        }
      } finally {
        this.syncLoading = false
      }
    },

    async fetchJobStatus(jobId) {
      if (!jobId) return
      this.syncPolling = true
      const token = this.$cookies.get('teamwrkr-token')
      try {
        const res = await this.axios.get(
          `${process.env.VUE_APP_API_URL}/work/ingest/${jobId}/status`,
          { headers: { Authorization: token } }
        )
        this.syncJob = res.data
        if (!this.isSyncRunning) this.stopPolling()
      } catch (err) {
        // Don't surface transient polling errors — just keep trying
      } finally {
        this.syncPolling = false
      }
    },

    startPolling(jobId) {
      this.stopPolling()
      this.syncPollingTimer = setInterval(() => this.fetchJobStatus(jobId), 4000)
    },

    stopPolling() {
      if (this.syncPollingTimer) {
        clearInterval(this.syncPollingTimer)
        this.syncPollingTimer = null
      }
    },
  },
}
</script>

<style lang="scss">
.company-container {
  padding: $space-6 0;
  text-align: left;
}

.company-card {
  @include card($color-surface, $color-border, $shadow-md);
  padding: $space-6;
}

.company-tabs {
  .tabs {
    justify-content: center;
    margin-bottom: $space-5;
  }

  .tab-content {
    padding: 0;
  }
}

.employees-card {
  padding: $space-5;
}

.company-details {
  display: flex;
  flex-direction: column;
  gap: $space-4;
  margin-bottom: $space-6;
}

.detail-label {
  display: block;
  font-size: $font-size-xs;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: $color-text-subtle;
  margin-bottom: $space-1;
}

.detail-value {
  font-size: $font-size-md;
  font-weight: 600;
  color: $color-text;
  word-break: break-word;
}

.invite-detail .invite-display {
  display: flex;
  align-items: center;
  gap: $space-3;
  flex-wrap: wrap;
}

.invite-detail .button {
  min-width: 80px;
}

.company-form {
  display: flex;
  flex-direction: column;
  gap: $space-4;
  margin-bottom: $space-5;
}

.daily-work-sync-field {
  .checkbox {
    color: $color-text;
    font-weight: 600;
  }
}

.daily-work-sync-control {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.daily-work-sync-help {
  color: $color-text;
  font-size: $font-size-sm;
  line-height: 1.5;
  margin: 0;
}

.daily-work-sync-help--warning {
  color: darken($color-warning, 20%);
  margin-top: $space-2;
}

.lwd-daily-email-field {
  .checkbox {
    color: $color-text;
    font-weight: 600;
  }
}

.lwd-daily-email-control {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.lwd-daily-email-help {
  color: $color-text;
  font-size: $font-size-sm;
  line-height: 1.5;
  margin: 0;
}

.signals-enabled-control {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.signals-enabled-help {
  color: $color-text;
  font-size: $font-size-sm;
  line-height: 1.5;
  margin: 0;
}

.company-form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: $space-4;
}

.copy-confirm {
  color: $color-success;
  font-weight: 600;
}

.work-sync-card {
  display: flex;
  flex-direction: column;
  gap: $space-6;
}

.work-sync-section__title {
  font-size: $font-size-lg;
  font-weight: 700;
  color: $color-text;
  margin: 0 0 $space-2;
}

.work-sync-section__desc {
  font-size: $font-size-sm;
  color: $color-text-muted;
  margin: 0 0 $space-4;
}

.work-sync-notice {
  display: flex;
  align-items: center;
  gap: $space-2;
  padding: $space-3 $space-4;
  background: rgba($color-warning, 0.12);
  color: darken($color-warning, 20%);
  border-radius: $radius-md;
  font-size: $font-size-sm;
  font-weight: 500;

  i { font-size: 1.1rem; }
}

.work-sync-form__row {
  display: flex;
  gap: $space-4;
  flex-wrap: wrap;

  .field { flex: 1; min-width: 160px; }
}

.work-sync-form__actions {
  margin-top: $space-2;
}

.work-sync-error {
  display: flex;
  align-items: center;
  gap: $space-2;
  margin-top: $space-3;
  color: $color-danger;
  font-size: $font-size-sm;

  i { font-size: 1rem; }
}

.work-sync-status {
  border-top: 1px solid $color-border;
  padding-top: $space-5;
}

.work-sync-status__header {
  display: flex;
  align-items: center;
  gap: $space-3;
  margin-bottom: $space-4;
  flex-wrap: wrap;
}

.work-sync-status__badge {
  display: inline-block;
  padding: 0.2rem 0.65rem;
  border-radius: $radius-pill;
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &.is-queued    { background: $color-surface-muted; color: $color-text-muted; }
  &.is-processing { background: $color-info-soft; color: $color-primary-deep; }
  &.is-completed  { background: $color-success-soft; color: $color-success-strong; }
  &.is-completed_with_errors { background: rgba($color-warning, 0.12); color: darken($color-warning, 20%); }
  &.is-failed     { background: rgba($color-danger, 0.08); color: $color-danger-strong; }
}

.work-sync-status__id {
  font-size: $font-size-xs;
  color: $color-text-subtle;
  font-family: monospace;
}

.work-sync-progress {
  margin-bottom: $space-4;

  .progress { margin-bottom: $space-1; }
}

.work-sync-progress__msg {
  font-size: $font-size-sm;
  color: $color-text-muted;
  margin: 0;
}

.work-sync-results__grid {
  display: flex;
  gap: $space-4;
  flex-wrap: wrap;
  margin-bottom: $space-3;
}

.work-sync-results__stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: $color-surface-alt;
  border: 1px solid $color-border;
  border-radius: $radius-md;
  padding: $space-3 $space-4;
  min-width: 90px;
}

.work-sync-results__num {
  font-size: 1.5rem;
  font-weight: 800;
  color: $color-primary;
  line-height: 1;
}

.work-sync-results__label {
  font-size: $font-size-xs;
  color: $color-text-muted;
  margin-top: $space-1;
  text-align: center;
}

.work-sync-results__errors {
  margin: 0;
  padding-left: 1.25rem;
  font-size: $font-size-sm;
  color: $color-danger-strong;
}

.form-actions {
  display: flex;
  align-items: center;
  gap: $space-4;
}

.save-message {
  font-weight: 600;
}

.save-message.success {
  color: $color-success;
}

.save-message.error {
  color: $color-accent;
}

.handle-repeater {
  display: flex;
  flex-direction: column;
  gap: $space-2;
  width: 100%;

  &__row {
    display: flex;
    align-items: center;
    gap: $space-2;
  }
}

.company-employees-table .empty-msg {
  text-align: center;
  padding: $space-6 0;
  color: $color-text-muted;
}
</style>
